import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import LangToggle from "@/components/LangToggle";
import ThemeToggle from "@/components/ThemeToggle";
import { ArrowLeft, ShieldCheck, Megaphone, Send, FlaskConical, Loader2, Users, AlertTriangle, Check } from "lucide-react";

export default function AdminCommunication() {
  const { user, loading: authLoading, theme } = useAuth();
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(null);
  const [stats, setStats] = useState(null);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/me`)
      .then((r) => {
        setAuthorized(!!r.data.is_admin);
        if (!r.data.is_admin) navigate("/dashboard", { replace: true });
      })
      .catch(() => { setAuthorized(false); navigate("/dashboard", { replace: true }); });
  }, [user, navigate]);

  useEffect(() => {
    if (!authorized) return;
    axios.get(`${API}/admin/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [authorized]);

  const send = async (mode) => {
    if (!subject.trim() || !message.trim()) {
      alert("Bitte Betreff und Nachricht ausfüllen.");
      return;
    }
    if (mode === "all" && !confirmAll) {
      setConfirmAll(true);
      return;
    }
    setSending(true);
    setPendingMode(mode);
    setResult(null);
    try {
      const r = await axios.post(`${API}/admin/broadcast`, { subject, message, mode });
      setResult(r.data);
      if (mode === "all") setConfirmAll(false);
    } catch (e) {
      alert("Senden fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setSending(false);
      setPendingMode(null);
    }
  };

  if (authLoading || authorized === null) {
    return <div className="min-h-screen flex items-center justify-center bg-bone"><Loader2 className="w-5 h-5 animate-spin text-pine" /></div>;
  }
  if (!authorized) return null;

  const totalRecipients = stats?.total_users ?? "—";

  return (
    <div className={`min-h-screen bg-bone ${theme === "dark" ? "dark" : ""}`} data-testid="admin-communication">
      <header className="h-16 bg-surface border-b border-hairline flex items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <div className="h-5 w-px bg-hairline" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-terracotta flex items-center justify-center text-bone">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div className="font-heading text-lg tracking-tight">Kommunikation</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <LangToggle />
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-terracotta/10 text-terracotta text-[10px] uppercase tracking-[0.2em] font-semibold">
            <Megaphone className="w-3 h-3" /> Broadcast
          </div>
          <h1 className="mt-3 font-heading text-4xl tracking-tight">Nachricht an Nutzer senden</h1>
          <p className="mt-2 text-ink-muted text-sm">
            Schicken Sie eine professionelle E-Mail an einzelne oder alle Therapeut:innen. Footer und Abmelde-Link werden automatisch eingefügt.
          </p>
        </motion.div>

        {/* Form */}
        <div className="bg-surface border border-hairline rounded-2xl p-6 space-y-5" data-testid="broadcast-form">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">Betreff</label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="z.B. Neue Funktion: Therapie-Verlauf"
              maxLength={150}
              className="mt-2 w-full px-4 py-2.5 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none text-sm"
              data-testid="broadcast-subject"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">Nachricht</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              placeholder="Liebe Therapeut:innen,&#10;&#10;wir haben eine neue Funktion ergänzt..."
              className="mt-2 w-full px-4 py-3 rounded-xl bg-bone border border-hairline focus:border-pine focus:outline-none text-sm leading-relaxed font-body resize-y"
              data-testid="broadcast-message"
            />
            <div className="mt-2 text-xs text-ink-muted">
              Hinweis: Die Anrede ("Hallo {"{Vorname}"}, ...") und der Schlussgruß ("Herzliche Grüße, Ihr PhysioSOAP-Team") werden automatisch ergänzt.
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={() => send("test")}
              disabled={sending || !subject.trim() || !message.trim()}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-hairline hover:bg-pine-50 disabled:opacity-50 transition text-sm font-medium"
              data-testid="broadcast-test-btn"
            >
              {sending && pendingMode === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Test-Mail an mich
            </button>
            <button
              onClick={() => send("all")}
              disabled={sending || !subject.trim() || !message.trim()}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition disabled:opacity-50 ${
                confirmAll
                  ? "bg-terracotta text-white hover:bg-terracotta/90"
                  : "bg-pine text-bone hover:bg-pine-hover"
              }`}
              data-testid="broadcast-all-btn"
            >
              {sending && pendingMode === "all" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : confirmAll ? (
                <AlertTriangle className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {confirmAll ? `Wirklich an ${totalRecipients} Nutzer senden?` : "An alle Nutzer senden"}
            </button>
            {confirmAll && !sending && (
              <button
                onClick={() => setConfirmAll(false)}
                className="text-sm text-ink-muted hover:text-ink"
                data-testid="broadcast-cancel-btn"
              >Abbrechen</button>
            )}
            <div className="ml-auto inline-flex items-center gap-2 text-xs text-ink-muted">
              <Users className="w-3.5 h-3.5" /> {totalRecipients} aktive Empfänger
            </div>
          </div>
        </div>

        {/* Result */}
        {result && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`rounded-2xl border p-5 ${
              result.failed > 0 ? "border-terracotta/40 bg-terracotta/5" : "border-pine/30 bg-pine-50"
            }`}
            data-testid="broadcast-result"
          >
            <div className="flex items-center gap-2 text-sm font-medium text-ink">
              {result.failed > 0 ? (
                <AlertTriangle className="w-4 h-4 text-terracotta" />
              ) : (
                <Check className="w-4 h-4 text-pine" />
              )}
              {result.mode === "test" ? "Test-Mail versendet" : "Broadcast abgeschlossen"}
            </div>
            <div className="mt-2 text-sm text-ink-muted">
              {result.sent} von {result.total} E-Mails erfolgreich versendet
              {result.failed > 0 ? `, ${result.failed} fehlgeschlagen` : ""}.
            </div>
          </motion.div>
        )}

        <div className="text-xs text-ink-muted leading-relaxed">
          <strong className="text-pine">Hinweis:</strong> Jede E-Mail enthält automatisch einen PhysioSOAP-Footer mit DSGVO-Hinweis und einen funktionierenden Abmelde-Link. Bereits abgemeldete Nutzer werden ausgeschlossen.
        </div>
      </div>
    </div>
  );
}
